import React, { useState } from 'react';
import {
  createStore,
  useStore,
  StateCreator,
  StoreApi,
  StoreMutatorIdentifier,
  Mutate,
} from 'zustand';

type State = Record<string, any>;

export function createContextStore<
  T extends State,
  Mos extends [StoreMutatorIdentifier, unknown][] = [],
>(initialState: StateCreator<T, [], Mos>) {
  const Context = React.createContext<Mutate<StoreApi<T>, Mos> | undefined>(undefined);

  /**
   * Provider component that wraps the app and provides the store. It creates a new store instance.
   */
  const Provider = ({ children }: { children?: React.ReactNode }) => {
    const [store] = useState(() => createStore(initialState));

    return <Context.Provider value={store}>{children}</Context.Provider>;
  };

  /**
   * Hook to access the store state using a selector.
   */
  function useContextStore<U>(selector: (store: T) => U) {
    const store = React.useContext(Context);
    if (!store) {
      throw new Error('useContextStore must be used within a Provider');
    }

    return useStore(store, selector);
  }

  /**
   * Hook to access the store API directly.
   */
  function useImperativeApi(): Mutate<StoreApi<T>, Mos> {
    const store = React.useContext(Context);

    if (!store) {
      throw new Error('useImperativeApi must be used within a Provider');
    }

    return store;
  }

  return { Provider, useContextStore, useImperativeApi };
}
